'use client';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { CloseIcon, MainButton, SecondaryButton } from '../../atomic-atoms';
import { Modal } from '../../atomic-molecules';

import styles from './DateRangePickerModal.module.scss';

import type { Dayjs } from 'dayjs';

export type DateRangePreset =
  | 'all_dates'
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'last_week'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'last_year'
  | 'custom';

export type DateRangePickerModalValue = {
  preset: DateRangePreset;
  startDate?: string | null;
  endDate?: string | null;
};

type DateRangePickerModalProps = {
  value?: DateRangePickerModalValue;
  defaultValue?: DateRangePickerModalValue;
  onApply: (value: DateRangePickerModalValue) => void;
  disabled?: boolean;
};

const DATE_FORMAT = 'YYYY-MM-DD';

const PRESET_OPTIONS: Array<{ value: DateRangePreset; label: string }> = [
  { value: 'all_dates', label: 'All Dates' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
];

const DEFAULT_VALUE: DateRangePickerModalValue = {
  preset: 'this_week',
  startDate: dayjs().startOf('week').format(DATE_FORMAT),
  endDate: dayjs().endOf('week').format(DATE_FORMAT),
};

function getPresetLabel(preset: DateRangePreset) {
  return (
    PRESET_OPTIONS.find((option) => option.value === preset)?.label ??
    'Custom Range'
  );
}

function toDayjs(value?: string | null) {
  return value ? dayjs(value) : null;
}

function toIsoDate(value: Dayjs | null) {
  return value?.format(DATE_FORMAT) ?? null;
}

function getPresetRange(preset: DateRangePreset): DateRangePickerModalValue {
  const today = dayjs();

  if (preset === 'all_dates') {
    return { preset, startDate: null, endDate: null };
  }

  if (preset === 'today') {
    const date = today.format(DATE_FORMAT);
    return { preset, startDate: date, endDate: date };
  }

  if (preset === 'yesterday') {
    const date = today.subtract(1, 'day').format(DATE_FORMAT);
    return { preset, startDate: date, endDate: date };
  }

  if (preset === 'this_week') {
    return {
      preset,
      startDate: today.startOf('week').format(DATE_FORMAT),
      endDate: today.endOf('week').format(DATE_FORMAT),
    };
  }

  if (preset === 'last_week') {
    const lastWeek = today.subtract(1, 'week');
    return {
      preset,
      startDate: lastWeek.startOf('week').format(DATE_FORMAT),
      endDate: lastWeek.endOf('week').format(DATE_FORMAT),
    };
  }

  if (preset === 'this_month') {
    return {
      preset,
      startDate: today.startOf('month').format(DATE_FORMAT),
      endDate: today.endOf('month').format(DATE_FORMAT),
    };
  }

  if (preset === 'last_month') {
    const lastMonth = today.subtract(1, 'month');
    return {
      preset,
      startDate: lastMonth.startOf('month').format(DATE_FORMAT),
      endDate: lastMonth.endOf('month').format(DATE_FORMAT),
    };
  }

  if (preset === 'this_year') {
    return {
      preset,
      startDate: today.startOf('year').format(DATE_FORMAT),
      endDate: today.endOf('year').format(DATE_FORMAT),
    };
  }

  if (preset === 'last_year') {
    const lastYear = today.subtract(1, 'year');
    return {
      preset,
      startDate: lastYear.startOf('year').format(DATE_FORMAT),
      endDate: lastYear.endOf('year').format(DATE_FORMAT),
    };
  }

  return DEFAULT_VALUE;
}

function getTriggerLabel(value: DateRangePickerModalValue) {
  if (value.preset !== 'custom') {
    return getPresetLabel(value.preset);
  }

  if (value.startDate && value.endDate) {
    const start = dayjs(value.startDate);
    const end = dayjs(value.endDate);

    if (start.year() === end.year()) {
      if (start.month() === end.month()) {
        return `${start.format('MMM D')} - ${end.format('D, YYYY')}`;
      }

      return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
    }

    return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
  }

  return 'Custom Range';
}

function isInRange(date: Dayjs, start: Dayjs | null, end: Dayjs | null) {
  if (!start || !end) {
    return false;
  }

  return date.isAfter(start, 'day') && date.isBefore(end, 'day');
}

function isBoundary(date: Dayjs, start: Dayjs | null, end: Dayjs | null) {
  return Boolean(
    (start && date.isSame(start, 'day')) || (end && date.isSame(end, 'day')),
  );
}

export function DateRangePickerModal({
  value,
  defaultValue = DEFAULT_VALUE,
  onApply,
  disabled = false,
}: DateRangePickerModalProps) {
  const actualValue = value ?? defaultValue;
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValue] =
    useState<DateRangePickerModalValue>(actualValue);

  const draftStart = useMemo(
    () => toDayjs(draftValue.startDate),
    [draftValue.startDate],
  );
  const draftEnd = useMemo(
    () => toDayjs(draftValue.endDate),
    [draftValue.endDate],
  );

  const openModal = () => {
    setDraftValue(actualValue);
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const selectPreset = (preset: DateRangePreset) => {
    setDraftValue(getPresetRange(preset));
  };

  const selectDate = (nextDate: Dayjs | null) => {
    if (!nextDate) {
      return;
    }

    if (!draftStart || draftEnd || nextDate.isBefore(draftStart, 'day')) {
      setDraftValue({
        preset: 'custom',
        startDate: toIsoDate(nextDate),
        endDate: null,
      });
      return;
    }

    setDraftValue({
      preset: 'custom',
      startDate: toIsoDate(draftStart),
      endDate: toIsoDate(nextDate),
    });
  };

  const applySelection = () => {
    onApply(draftValue);
    closeModal();
  };

  return (
    <>
      <SecondaryButton
        ariaLabel="Open date range picker"
        onClick={openModal}
        disabled={disabled}
        style={{ maxWidth: '100%', minWidth: 0, width: '100%' }}
      >
        <span className={styles.triggerLabel}>
          {getTriggerLabel(actualValue)}
        </span>
      </SecondaryButton>

      <Modal open={isOpen} onClose={closeModal} width="min(760px, 94vw)">
        <div className={styles.modalCard}>
          <header className={styles.header}>
            <div>
              <h2>Date range</h2>
              <p>Pick a start and end date, or use a quick preset.</p>
            </div>

            <button
              type="button"
              className={styles.closeButton}
              aria-label="Close date range picker"
              onClick={closeModal}
            >
              <CloseIcon size={14} />
            </button>
          </header>

          <div className={styles.body}>
            <aside className={styles.presets}>
              <span className={styles.sectionLabel}>Presets</span>
              <div className={styles.presetList}>
                {PRESET_OPTIONS.map((option) => (
                  <SecondaryButton
                    key={option.value}
                    ariaLabel={`Select ${option.label}`}
                    style={{ width: '100%' }}
                    backgroundOverride={
                      draftValue.preset === option.value ? 'emphasis' : 'theme'
                    }
                    onClick={() => selectPreset(option.value)}
                  >
                    {option.label}
                  </SecondaryButton>
                ))}
              </div>
            </aside>

            <section className={styles.calendarArea}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  value={draftStart ?? draftEnd ?? dayjs()}
                  onChange={selectDate}
                  slots={{
                    day: (props) => {
                      const inRange = isInRange(
                        props.day,
                        draftStart,
                        draftEnd,
                      );
                      const boundary = isBoundary(
                        props.day,
                        draftStart,
                        draftEnd,
                      );

                      return (
                        <button
                          type="button"
                          className={styles.calendarDay}
                          data-in-range={inRange || undefined}
                          data-boundary={boundary || undefined}
                          data-outside={props.outsideCurrentMonth || undefined}
                          disabled={props.outsideCurrentMonth}
                          onClick={() => selectDate(props.day)}
                        >
                          {props.day.date()}
                        </button>
                      );
                    },
                  }}
                />
              </LocalizationProvider>
            </section>

            <aside className={styles.selectionPanel}>
              <span className={styles.sectionLabel}>Selection</span>
              <div className={styles.selectionCard}>
                <span>Start date</span>
                <strong>{draftValue.startDate ?? '-'}</strong>
                <span>End date</span>
                <strong>{draftValue.endDate ?? '-'}</strong>
              </div>
              <div className={styles.tipCard}>
                <strong>Tip</strong>
                <p>
                  Click a start date, then an end date. Clicking a new date
                  after selecting a range starts a fresh selection.
                </p>
              </div>
            </aside>
          </div>

          <footer className={styles.footer}>
            <div className={styles.footerActions}>
              <SecondaryButton
                ariaLabel="Cancel date range"
                onClick={closeModal}
              >
                Cancel
              </SecondaryButton>
              <MainButton ariaLabel="Apply date range" onClick={applySelection}>
                Apply
              </MainButton>
            </div>
          </footer>
        </div>
      </Modal>
    </>
  );
}
